import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Leaf, Recycle, Cloud, Trophy } from "lucide-react";
import { ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis } from "recharts";

const BenchmarkBar = ({ label, value, avg, top, unit = "%" }) => {
  const max = Math.max(top, value, avg) * 1.1;
  const pct = (n) => `${Math.min(100, (n / max) * 100)}%`;
  const status = value >= top ? "success" : value >= avg ? "forest" : "warn";
  const statusLabel = value >= top ? "Top quartile" : value >= avg ? "Above average" : "Below average";
  return (
    <div className="py-3">
      <div className="flex items-baseline justify-between mb-2">
        <div className="hbt-label">{label}</div>
        <span className={`pill ${status}`}>{statusLabel}</span>
      </div>
      <div className="relative h-8 rounded-md bg-neutral-100 overflow-hidden">
        <div className="absolute top-0 bottom-0 border-l border-dashed" style={{ left: pct(avg), borderColor: "rgba(10,10,10,0.35)" }}>
          <div className="absolute -top-4 -translate-x-1/2 text-[9px] uppercase tracking-widest text-neutral-500 whitespace-nowrap">avg {avg}{unit}</div>
        </div>
        <div className="absolute top-0 bottom-0 border-l" style={{ left: pct(top), borderColor: "var(--forest)" }}>
          <div className="absolute -bottom-4 -translate-x-1/2 text-[9px] uppercase tracking-widest whitespace-nowrap" style={{ color: "var(--forest)" }}>top {top}{unit}</div>
        </div>
        <div className="h-full rounded-md" style={{ width: pct(value), background: value >= avg ? "var(--forest)" : "var(--terracotta)", opacity: 0.85 }} />
        <div className="absolute inset-y-0 flex items-center px-3">
          <span className="text-xs font-bold font-mono-num" style={{ color: value / max < 0.35 ? "var(--ink)" : "white" }}>
            {value}{unit}
          </span>
        </div>
      </div>
    </div>
  );
};

export default function Sustainability() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/clients").then((r) => {
        setClients(r.data);
        if (r.data.length) setClientId(r.data[0].id);
      });
    } else if (user?.client_id) {
      setClientId(user.client_id);
    }
  }, [user]);

  const generate = () => {
    if (!clientId) return;
    setLoading(true);
    setData(null);
    api.get(`/analytics/insights?client_id=${clientId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not fetch insights"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (clientId) generate(); }, [clientId]); // eslint-disable-line

  const s = data?.summary;
  const b = s?.benchmark;

  return (
    <div>
      <PageHeader eyebrow="ESG Intelligence" title="Sustainability insights" subtitle="AI-generated recommendations powered by Claude Sonnet 4.5." />
      <div className="px-8 lg:px-12 py-8 space-y-6">
        {user?.role === "admin" && (
          <div className="hbt-card p-6">
            <label className="hbt-label block mb-2">Analyse client</label>
            <select className="hbt-input max-w-md" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="insights-client-select">
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}

        {s && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 stagger">
            <div className="hbt-card p-6 flex flex-col justify-between min-h-[180px]">
              <div className="flex items-center justify-between">
                <div className="hbt-label">ESG Score</div>
                <Trophy className="w-4 h-4" style={{ color: "var(--terracotta)" }} />
              </div>
              <div className="mt-4 flex items-center gap-4">
                <div className="w-24 h-24">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ v: s.esg_score, fill: "#C85A17" }]} startAngle={90} endAngle={-270}>
                      <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                      <RadialBar dataKey="v" cornerRadius={50} background={{ fill: "#F0EAD6" }} />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <div className="font-display text-4xl font-semibold" style={{ color: "var(--terracotta)" }}>{s.esg_score}</div>
                  <div className="text-xs text-neutral-500 uppercase tracking-widest mt-1">out of 100</div>
                </div>
              </div>
            </div>

            <div className="hbt-card p-6">
              <div className="flex items-center justify-between">
                <div className="hbt-label">Diversion</div>
                <Recycle className="w-4 h-4" style={{ color: "var(--forest)" }} />
              </div>
              <div className="font-display text-4xl font-semibold mt-4" style={{ color: "var(--forest)" }}>{s.diversion_rate}%</div>
              <div className="text-xs text-neutral-500 mt-2">{s.diverted_kg} kg diverted from landfill</div>
            </div>

            <div className="hbt-card p-6">
              <div className="flex items-center justify-between">
                <div className="hbt-label">Recycling</div>
                <Leaf className="w-4 h-4" style={{ color: "var(--forest)" }} />
              </div>
              <div className="font-display text-4xl font-semibold mt-4" style={{ color: "var(--forest)" }}>{s.recycling_rate}%</div>
              <div className="text-xs text-neutral-500 mt-2">{s.recycling_kg} kg recycled</div>
            </div>

            <div className="hbt-card p-6">
              <div className="flex items-center justify-between">
                <div className="hbt-label">CO₂e saved</div>
                <Cloud className="w-4 h-4" style={{ color: "var(--forest)" }} />
              </div>
              <div className="font-display text-4xl font-semibold mt-4 font-mono-num" style={{ color: "var(--forest)" }}>{(s.co2_saved_kg/1000).toFixed(2)}<span className="text-lg text-neutral-500 ml-1">t</span></div>
              <div className="text-xs text-neutral-500 mt-2">estimated emissions avoided</div>
            </div>
          </div>
        )}

        {s?.by_stream && s.by_stream.length > 0 && (
          <div className="hbt-card p-8" data-testid="streams-breakdown">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="hbt-label">Waste streams</div>
                <div className="font-display text-2xl font-semibold mt-1">Breakdown by stream</div>
                <div className="text-xs text-neutral-500 mt-1">Every specific waste stream, from PET plastic to used cooking oil, ranked by volume.</div>
              </div>
              <div className="text-xs text-neutral-500 font-mono-num shrink-0">{s.by_stream.length} streams</div>
            </div>
            <div className="space-y-2">
              {(() => {
                const topKg = s.by_stream[0]?.kg || 1;
                return s.by_stream.map((row) => {
                  const pct = Math.max(2, Math.round((row.kg / topKg) * 100));
                  const color = row.waste_type === "recyclable" ? "var(--forest)" : row.waste_type === "hazardous" ? "var(--terracotta)" : row.waste_type === "organic" ? "#7B9E6B" : "#5B6663";
                  return (
                    <div key={row.key} className="grid grid-cols-[minmax(160px,1fr)_2fr_120px] gap-3 items-center py-1" data-testid={`stream-row-${row.key}`}>
                      <div className="text-sm truncate" title={row.name}>{row.name}</div>
                      <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
                      </div>
                      <div className="text-right font-mono-num text-sm font-semibold">{row.kg.toLocaleString(undefined, { maximumFractionDigits: 1 })} <span className="text-neutral-400">{row.unit}</span></div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        <div className="hbt-card p-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="hbt-label flex items-center gap-2"><Sparkles className="w-3 h-3" style={{ color: "var(--terracotta)" }} /> AI Advisor</div>
              <div className="font-display text-2xl font-semibold mt-1">ESG analysis & recommendations</div>
              {data?.generated_at && <div className="text-xs text-neutral-500 mt-1">Generated {new Date(data.generated_at).toLocaleString()}</div>}
            </div>
            <button onClick={generate} disabled={loading} className="btn-outline inline-flex items-center gap-2" data-testid="regenerate-insights-btn">
              <Sparkles className="w-4 h-4" /> {loading ? "Thinking…" : "Regenerate"}
            </button>
          </div>

          {loading && <div className="text-sm text-neutral-500">Claude is analysing your data…</div>}
          {!loading && data?.insights && (
            <div className="prose prose-sm max-w-none" data-testid="insights-text">
              {data.insights.split("\n").map((line, i) => (
                <p key={i} className="text-[15px] leading-[1.75] mb-3 text-neutral-800">{line}</p>
              ))}
            </div>
          )}
          {!loading && !data?.insights && <div className="text-sm text-neutral-500">Select a client to generate insights.</div>}
        </div>

        {b && (
          <div className="hbt-card p-8" data-testid="benchmark-card">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="hbt-label">Benchmark</div>
                <div className="font-display text-2xl font-semibold mt-1">Industry comparison</div>
                <div className="text-xs text-neutral-500 mt-1">Benchmarked against <b>{b.industry}</b> sector averages (EPA, WRAP UK, Ellen MacArthur Foundation).</div>
              </div>
            </div>
            <div className="space-y-4">
              <BenchmarkBar label="Landfill diversion" value={s.diversion_rate} avg={b.diversion_avg} top={b.diversion_top} />
              <BenchmarkBar label="Recycling rate" value={s.recycling_rate} avg={b.recycling_avg} top={b.recycling_top} />
              <BenchmarkBar label="ESG score" value={s.esg_score} avg={b.esg_avg} top={b.esg_top} unit="" />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4 text-xs">
              <div className="hbt-card p-4">
                <div className="hbt-label">Diversion Δ vs avg</div>
                <div className={`font-display text-2xl font-semibold mt-2 font-mono-num ${b.diversion_delta >= 0 ? "" : "text-[var(--error)]"}`} style={b.diversion_delta >= 0 ? { color: "var(--success)" } : {}}>{b.diversion_delta >= 0 ? "+" : ""}{b.diversion_delta}%</div>
              </div>
              <div className="hbt-card p-4">
                <div className="hbt-label">Recycling Δ vs avg</div>
                <div className={`font-display text-2xl font-semibold mt-2 font-mono-num`} style={b.recycling_delta >= 0 ? { color: "var(--success)" } : { color: "var(--error)" }}>{b.recycling_delta >= 0 ? "+" : ""}{b.recycling_delta}%</div>
              </div>
              <div className="hbt-card p-4">
                <div className="hbt-label">ESG Δ vs avg</div>
                <div className={`font-display text-2xl font-semibold mt-2 font-mono-num`} style={b.esg_delta >= 0 ? { color: "var(--success)" } : { color: "var(--error)" }}>{b.esg_delta >= 0 ? "+" : ""}{b.esg_delta}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
